import { Plus, SlidersHorizontal } from "@phosphor-icons/react";
import clsx from "clsx";
import { Fragment, type JSX, useEffect, useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuList,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  dropdownMenuItemVariants,
} from "common/components/ui/DropdownMenu";
import {
  getWhitelabelCustomization,
  typedObjectEntries,
} from "common/helpers/utils";

export default function DesktopFiltersDropdown<
  Filters extends Record<
    string,
    { label: string; icon: JSX.Element; isBoolean?: true }
  >,
>({
  filters,
  renderFilter,
  hasFilters,
}: {
  filters: Filters;
  renderFilter: (filterKey: keyof Filters) => JSX.Element;
  hasFilters: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [triggerStyle, setTriggerStyle] = useState({ bottom: 0, left: 0 });

  useEffect(() => {
    function handleResize() {
      setIsOpen(false);
    }

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      <button
        type="button"
        className={clsx(
          "border gap-1 h-6 bg-whiteGray pl-0.75 border-black-200 hover:bg-black-100 rounded-lg items-center flex text-button-xs text-black-950 transition-[background] duration-300 ease-in-out",
          hasFilters ? "pr-0.75" : "pr-2",
          isOpen &&
            (getWhitelabelCustomization()?.primary_color
              ? "border-whitelabel-primary"
              : "border-purple-500"),
          "disabled:bg-white disabled:text-black-500",
        )}
        onClick={(e) => {
          setTriggerStyle({
            left:
              e.currentTarget.getBoundingClientRect().left -
              e.currentTarget.parentElement.getBoundingClientRect().left,
            bottom:
              e.currentTarget.getBoundingClientRect().bottom -
              e.currentTarget.parentElement.getBoundingClientRect().bottom,
          });
          setIsOpen(true);
        }}
        aria-label={hasFilters && "filters"}
      >
        {hasFilters ? (
          <Plus className="size-4" />
        ) : (
          <SlidersHorizontal weight="fill" className="size-4" />
        )}
        {!hasFilters && "Filters"}
      </button>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger
          style={triggerStyle}
          className="absolute"
          tabIndex={-1}
        />
        <DropdownMenuContent
          align="start"
          sideOffset={4}
          size="sm"
          className="w-60"
        >
          <DropdownMenuList isScrollable={false}>
            {typedObjectEntries(filters).map(
              ([key, { icon, label, isBoolean }]) => {
                const itemContent = (
                  <>
                    {icon}
                    {label}
                  </>
                );

                return (
                  <Fragment key={key as string}>
                    {isBoolean ? (
                      <div
                        className={dropdownMenuItemVariants({
                          variant: "default",
                          className: "hover:bg-transparent hover:cursor-auto",
                          size: "sm",
                        })}
                      >
                        {itemContent}
                        {renderFilter(key)}
                      </div>
                    ) : (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger>
                          {itemContent}
                        </DropdownMenuSubTrigger>

                        <DropdownMenuSubContent
                          className="w-60"
                          size="sm"
                          sideOffset={12}
                        >
                          {renderFilter(key)}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    )}
                  </Fragment>
                );
              },
            )}
          </DropdownMenuList>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
